import { createContext, useContext, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { RoomEvent } from '../constants/matrixEvents';
import { useMatrixClient } from './MatrixContext';
import { useI18n } from './I18nContext';
import { avatarManager, invalidateAvatar as invalidateAvatarManager } from '../utils/avatarManager';
import { generateInitialsAvatar } from '../utils/avatar';
import { selectFallbackMembers } from '../utils/roomAvatarFallback';
import { isServerNoticesUser, isServerNoticesRoom, getServerNoticesAvatar } from '../utils/serverNoticesUtils';
import logger from '../utils/logger';
import { ALLOWED_IMAGE_TYPES, MAX_AVATAR_SIZE_BYTES, MAX_AVATAR_SIZE_MB } from '../constants';

/**
 * AvatarContext - Simplified avatar management API
 *
 * This context wraps the existing avatarManager to provide:
 * - Simple, high-level APIs (no manual acquire/release)
 * - Clear separation between explicit room avatars and fallback member avatars
 * - Automatic lifecycle management
 * - Consistent error handling
 *
 * Key Concepts:
 * - Explicit room avatar: MXC URL stored on Matrix server (can be null/empty)
 * - Fallback avatars: Client-side display using member avatars when no explicit avatar exists
 * - Default avatar behavior:
 *   - 1 member (you only): Your avatar
 *   - 2 members (you + 1): Their avatar
 *   - 3+ members: First two other members' avatars
 */
const AvatarContext = createContext(null);

export const AvatarProvider = ({ children }) => {
  const client = useMatrixClient();
  const { t } = useI18n();

  // Track all acquired avatars for automatic cleanup on unmount
  const acquiredAvatars = useRef(new Set()); // Set of userIds/roomIds

  /**
   * Validates image file dimensions
   * @private
   * @param {File} file - The image file to validate
   * @returns {Promise<{width: number, height: number}>} Image dimensions
   * @throws {Error} If dimensions exceed limit or image is invalid
   */
  const validateImageDimensions = useCallback(async (file) => {
    const MAX_DIMENSION = 4096;
    const TIMEOUT_MS = 180_000; // 180 second timeout
    const objectUrl = URL.createObjectURL(file);

    try {
      // Create dimension validation promise
      const dimensionPromise = new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          resolve({ width: img.width, height: img.height });
        };
        img.onerror = () => {
          reject(new Error(t('AvatarValidation.invalidImage')));
        };
        img.src = objectUrl;
      });

      // Create timeout promise
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          reject(new Error(t('AvatarValidation.validationTimeout')));
        }, TIMEOUT_MS);
      });

      // Race between dimension check and timeout
      const dimensions = await Promise.race([dimensionPromise, timeoutPromise]);

      if (dimensions.width > MAX_DIMENSION || dimensions.height > MAX_DIMENSION) {
        throw new Error(t('AvatarValidation.dimensionsTooLarge', { maxDimension: MAX_DIMENSION }));
      }

      logger.debug('Image dimensions validated:', dimensions.width, 'x', dimensions.height);
      return dimensions;
    } finally {
      // Clean up object URL
      URL.revokeObjectURL(objectUrl);
    }
  }, [t]);

  /**
   * Get a user's avatar
   * Automatically handles caching, fetching, and cleanup
   *
   * @param {string} userId - Matrix user ID
   * @param {string} displayName - Display name for initials fallback
   * @param {number} [size=96] - Avatar size in pixels
   * @returns {Promise<string>} Blob URL or data URL
   */
  const getUserAvatar = useCallback(async (userId, displayName, size = 96) => {
    if (!client || !userId) {
      return generateInitialsAvatar(displayName || userId, size);
    }

    // Check if this is a server notices user
    if (isServerNoticesUser(userId)) {
      return getServerNoticesAvatar();
    }

    try {
      const url = await avatarManager.acquire(client, userId, displayName, size, 'user');

      acquiredAvatars.current.add(userId);
      return url;
    } catch (error) {
      logger.error(`Failed to get user avatar for ${userId}:`, error);
      return generateInitialsAvatar(displayName || userId, size);
    }
  }, [client]);

  /**
   * Get a room's EXPLICIT avatar (what's stored on Matrix server)
   * Returns null if no explicit avatar is set
   * Use getFallbackAvatars() for member avatar fallback logic
   *
   * @param {string} roomId - Matrix room ID
   * @param {string} roomName - Room name for initials fallback
   * @param {number} [size=40] - Avatar size in pixels
   * @returns {Promise<string|null>} Blob URL, data URL, or null if no explicit avatar
   */
  const getRoomAvatar = useCallback(async (roomId, roomName, size = 40) => {
    if (!client || !roomId) {
      return null;
    }

    try {
      const room = client.getRoom(roomId);
      if (!room) {
        logger.warn(`Room ${roomId} not found`);
        return null;
      }

      // Check if this is a server notices room first
      if (isServerNoticesRoom(room, client)) {
        return getServerNoticesAvatar();
      }

      // Check if room has an explicit avatar MXC URL
      const roomAvatarMxc = room.getMxcAvatarUrl();

      if (!roomAvatarMxc) {
        // No explicit avatar set on server
        return null;
      }

      // Room has explicit avatar - fetch it
      const url = await avatarManager.acquire(client, roomId, roomName, size, 'room');
      acquiredAvatars.current.add(roomId);
      return url;
    } catch (error) {
      logger.error(`Failed to get room avatar for ${roomId}:`, error);
      return null;
    }
  }, [client]);

  /**
   * Get fallback avatars for a room (member avatars)
   * This is the client-side "default" avatar when no explicit room avatar exists
   *
   * Logic:
   * - 1 member (you only): Your avatar
   * - 2 members (you + 1 other): The other person's avatar
   * - 3+ members: First two other members' avatars
   *
   * @param {string} roomId - Matrix room ID
   * @param {number} [size=96] - Avatar size in pixels
   * @returns {Promise<Array<{userId: string, url: string}>>} Array of avatar descriptors
   */
  const getFallbackAvatars = useCallback(async (roomId, size = 96) => {
    if (!client || !roomId) {
      return [];
    }

    try {
      const room = client.getRoom(roomId);
      if (!room) {
        logger.warn(`Room ${roomId} not found`);
        return [];
      }

      const myUserId = client.getUserId();
      // Get all members including invited ones (not just joined)
      const allMembers = room.getMembers()
        .filter(member => member.membership === 'join' || member.membership === 'invite');

      if (!allMembers || allMembers.length === 0) {
        return [];
      }

      // Use utility function to select which members to display
      const isCurrentUser = (userId) => userId === myUserId;
      const membersToFetch = selectFallbackMembers(allMembers, isCurrentUser);

      // Fetch all avatars in parallel
      const avatarPromises = membersToFetch.map(async (member) => {
        const displayName = member.rawDisplayName || member.name || member.userId;
        try {
          const url = await avatarManager.acquire(client, member.userId, displayName, size, 'user');
          acquiredAvatars.current.add(member.userId);
          return {
            userId: member.userId,
            url,
          };
        } catch (error) {
          logger.error(`Failed to get fallback avatar for ${member.userId}:`, error);
          return {
            userId: member.userId,
            url: generateInitialsAvatar(displayName, size),
          };
        }
      });

      const avatars = await Promise.all(avatarPromises);
      return avatars.filter(Boolean);
    } catch (error) {
      logger.error(`Failed to get fallback avatars for ${roomId}:`, error);
      return [];
    }
  }, [client]);

  /**
   * Get fallback avatars from a member list (for room creation/edit scenarios)
   * This allows calculating fallback avatars before a room exists or for selected members
   *
   * @param {Array<Object>} members - Array of member objects with userId property
   * @param {number} [size=96] - Avatar size in pixels
   * @returns {Promise<Array<{userId: string, url: string}>>} Array of avatar descriptors
   *
   * @example
   * // For room creation with selected users
   * const selectedUsers = [{userId: '@alice:server', name: 'Alice'}, {userId: '@bob:server', name: 'Bob'}];
   * const avatars = await getFallbackAvatarsFromMembers(selectedUsers, 96);
   */
  const getFallbackAvatarsFromMembers = useCallback(async (members, size = 96) => {
    if (!client || !Array.isArray(members) || members.length === 0) {
      return [];
    }

    try {
      const myUserId = client.getUserId();

      // Use utility function to select which members to display
      const isCurrentUser = (userId) => userId === myUserId;
      const membersToFetch = selectFallbackMembers(members, isCurrentUser);

      // Fetch all avatars in parallel
      const avatarPromises = membersToFetch.map(async (member) => {
        const displayName = member.rawDisplayName || member.name || member.userId;
        try {
          const url = await avatarManager.acquire(client, member.userId, displayName, size, 'user');
          acquiredAvatars.current.add(member.userId);
          return {
            userId: member.userId,
            url,
          };
        } catch (error) {
          logger.error(`Failed to get fallback avatar for ${member.userId}:`, error);
          return {
            userId: member.userId,
            url: generateInitialsAvatar(displayName, size),
          };
        }
      });

      const avatars = await Promise.all(avatarPromises);
      return avatars.filter(Boolean);
    } catch (error) {
      logger.error('Failed to get fallback avatars from member list:', error);
      return [];
    }
  }, [client]);

  /**
   * Upload and set the current user's avatar
   *
   * @param {File} file - Image file to upload
   * @returns {Promise<string>} MXC URL of uploaded avatar
   */
  const setUserAvatar = useCallback(async (file) => {
    if (!client) {
      throw new Error('Matrix client not available');
    }

    if (!file || !ALLOWED_IMAGE_TYPES.includes(file.type)) {
      throw new Error(t('AvatarValidation.mustBeImage'));
    }

    // Validate file size
    if (file.size > MAX_AVATAR_SIZE_BYTES) {
      throw new Error(t('AvatarValidation.fileTooLarge', { maxSize: MAX_AVATAR_SIZE_MB }));
    }

    // Validate image dimensions (max 4096x4096)
    await validateImageDimensions(file);

    try {
      logger.info('Uploading user avatar:', file.name);

      // Upload file to Matrix content repository
      const uploadResponse = await client.uploadContent(file, {
        name: file.name,
        type: file.type,
      });

      const mxcUrl = uploadResponse.content_uri || uploadResponse;
      logger.debug('User avatar uploaded, MXC URL:', mxcUrl);

      // Invalidate cache BEFORE setting avatar to prevent race condition
      const myUserId = client.getUserId();
      await invalidateAvatarManager(myUserId);

      // Set user avatar
      await client.setAvatarUrl(mxcUrl);
      logger.info('User avatar set successfully:', mxcUrl);

      // Dispatch custom event for immediate UI updates
      window.dispatchEvent(new CustomEvent('matrix:avatar:changed', {
        detail: { userId: myUserId, mxcUrl },
      }));

      return mxcUrl;
    } catch (error) {
      logger.error('Failed to upload user avatar:', error);
      throw error;
    }
  }, [client, t, validateImageDimensions]);

  /**
   * Upload and set a room's avatar
   *
   * @param {string} roomId - Matrix room ID
   * @param {File} file - Image file to upload
   * @returns {Promise<string>} MXC URL of uploaded avatar
   */
  const setRoomAvatar = useCallback(async (roomId, file) => {
    if (!client || !roomId) {
      throw new Error('Matrix client or room ID not available');
    }

    if (!file || !ALLOWED_IMAGE_TYPES.includes(file.type)) {
      throw new Error(t('AvatarValidation.mustBeImage'));
    }

    // Validate file size
    if (file.size > MAX_AVATAR_SIZE_BYTES) {
      throw new Error(t('AvatarValidation.fileTooLarge', { maxSize: MAX_AVATAR_SIZE_MB }));
    }

    // Validate image dimensions (max 4096x4096)
    await validateImageDimensions(file);

    try {
      logger.info('Uploading room avatar:', roomId, file.name);

      // Upload file to Matrix content repository
      const uploadResponse = await client.uploadContent(file, {
        name: file.name,
        type: file.type,
      });

      const mxcUrl = uploadResponse.content_uri || uploadResponse;
      logger.debug('Room avatar uploaded, MXC URL:', mxcUrl);

      // Invalidate cache BEFORE setting room avatar to prevent race condition
      await invalidateAvatarManager(roomId);

      // Set room avatar via state event
      await client.sendStateEvent(
        roomId,
        'm.room.avatar',
        { url: mxcUrl },
        '',
      );
      logger.info('Room avatar set successfully:', roomId, mxcUrl);

      return mxcUrl;
    } catch (error) {
      logger.error('Failed to upload room avatar:', error);
      throw error;
    }
  }, [client, t, validateImageDimensions]);

  /**
   * Remove a room's avatar (set to empty)
   *
   * @param {string} roomId - Matrix room ID
   */
  const removeRoomAvatar = useCallback(async (roomId) => {
    if (!client || !roomId) {
      throw new Error('Matrix client or room ID not available');
    }

    try {
      logger.info('Removing room avatar:', roomId);

      // Invalidate cache BEFORE removing room avatar to prevent race condition
      await invalidateAvatarManager(roomId);

      // Remove avatar by setting empty URL
      await client.sendStateEvent(
        roomId,
        'm.room.avatar',
        { url: '' },
        '',
      );

      logger.info('Room avatar removed successfully:', roomId);
    } catch (error) {
      logger.error('Failed to remove room avatar:', error);
      throw error;
    }
  }, [client]);

  /**
   * Invalidate cached avatar (forces re-fetch)
   *
   * @param {string} id - User ID or room ID
   */
  const invalidateAvatar = useCallback(async (id) => {
    try {
      await invalidateAvatarManager(id);

      logger.debug(`Avatar cache invalidated for ${id}`);
    } catch (error) {
      logger.error(`Failed to invalidate avatar cache for ${id}:`, error);
    }
  }, []);

  /**
   * Release an avatar (decrement reference count)
   * Call this when you're done using an avatar
   *
   * @param {string} id - User ID or room ID
   */
  const releaseAvatar = useCallback((id) => {
    try {
      avatarManager.release(id);
      acquiredAvatars.current.delete(id);
      logger.debug(`Avatar released: ${id}`);
    } catch (error) {
      logger.error(`Failed to release avatar for ${id}:`, error);
    }
  }, []);

  /**
   * Get avatar manager statistics (for debugging)
   */
  const getAvatarStats = useCallback(() => {
    return avatarManager.getStats();
  }, []);

  // Listen for server notices room membership changes to fix avatar display
  // When a user first receives a server notice, the room goes from invite→join
  // and the avatar needs to be refreshed to show the special SVG
  useEffect(() => {
    if (!client) {
      return;
    }

    const handleMembershipChange = (room, membership) => {
      // Only handle rooms transitioning TO 'join' state
      if (membership !== 'join') {
        return;
      }

      // Check if this is a server notices room
      if (!isServerNoticesRoom(room, client)) {
        return;
      }

      const roomId = room.roomId;
      logger.info(`[AvatarContext] Server notices room joined, triggering avatar refresh: ${roomId}`);

      // Emit a custom event to trigger avatar refresh in useChannels and useRoomList
      // This ensures the special server notices avatar displays immediately
      const event = new CustomEvent('matrix:room:avatar:refresh', {
        detail: { roomId },
      });
      window.dispatchEvent(event);
    };

    client.on(RoomEvent.MyMembership, handleMembershipChange);

    return () => {
      client.off(RoomEvent.MyMembership, handleMembershipChange);
    };
  }, [client]);

  // Cleanup all acquired avatars on unmount
  useEffect(() => {
    return () => {
      logger.debug(`AvatarProvider unmounting, releasing ${acquiredAvatars.current.size} avatars`);
      for (const id of acquiredAvatars.current) {
        try {
          avatarManager.release(id);
        } catch (error) {
          logger.error(`Failed to release avatar ${id} on unmount:`, error);
        }
      }
      acquiredAvatars.current.clear();
    };
  }, []);

  const value = {
    getUserAvatar,
    getRoomAvatar,
    getFallbackAvatars,
    getFallbackAvatarsFromMembers,
    setUserAvatar,
    setRoomAvatar,
    removeRoomAvatar,
    invalidateAvatar,
    releaseAvatar,
    getAvatarStats,
  };

  return (
    <AvatarContext.Provider value={value}>
      {children}
    </AvatarContext.Provider>
  );
};

AvatarProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Hook to access avatar context
 * @returns {Object} Avatar context methods
 */
export const useAvatar = () => {
  const context = useContext(AvatarContext);
  if (!context) {
    throw new Error('useAvatar must be used within an AvatarProvider');
  }
  return context;
};

export default AvatarContext;
